import React, { useContext, useEffect, useState } from 'react';
import { Alert, Box, Header, Pagination, Table, TableProps } from '@amzn/awsui-components-react';
import CONSTANTS from 'src/utils/constants';
import ErrorUtils from 'src/utils/ErrorUtils';
import { DASOutputRow, GetDerivedAllocationSheetOutputResponse } from 'src/models/derived-allocation-sheet/DASLifecycle';
import { DerivedAllocationSheetData } from 'src/models/derived-allocation-sheet/DerivedAllocationSheet';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';

/** Segments per page; a sheet can map thousands. */
export const OUTPUT_PAGE_SIZE = 50;
export const NOT_EXECUTED_MESSAGE = 'Execute the sheet to see how each segment is allocated.';

/** The segment type as a lower-case noun for the title, e.g. "cost center" for COST_CENTER. */
export const segmentNoun = (segmentType?: string) => (segmentType ? segmentType.toLowerCase().replace(/_/g, ' ') : 'segment');

/**
 * The allocation sheet of the open version from a run, as Download picks it: the draft's own preview, or the output
 * that went live with any other version.
 */
export const outputKeyOf = (sheet: DerivedAllocationSheetData) => (sheet.sheetVersion === CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION
    ? sheet.draftOutputS3Url : sheet.outputS3Url);

/**
 * Step 4 "Allocation by segment", as in the mock: each segment of the last run's allocation sheet with its allocation
 * group and each recipient company's share, which is what the engine applies to the segment's balance. It reads the
 * file Download returns, a page at a time, so it shows exactly what the run wrote. Before a run it says to execute.
 */
export default function DasAllocationOutput() {
    const { dasState, services, sheetId } = useContext(DerivedAllocationSheetBuilderContext);
    // The builder renders the steps only once a sheet is loaded.
    const sheet = dasState.sheet!;
    const outputKey = outputKeyOf(sheet);
    const executed = !!outputKey;
    // The page picked for one output; a new run writes a new file, which starts again from page 1.
    const [picked, setPicked] = useState({ key: outputKey, page: 1 });
    const page = picked.key === outputKey ? picked.page : 1;
    const [result, setResult] = useState(undefined as GetDerivedAllocationSheetOutputResponse | undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined as string | undefined);

    useEffect(() => {
        if (!executed) return undefined;
        let active = true;
        setLoading(true);
        setError(undefined);
        // Drop the previous page, so a failed read never shows another file's rows as current.
        setResult(undefined);
        services.derivedAllocationSheetService.getDerivedAllocationSheetOutput({
            sheetId, version: sheet.sheetVersion, page, pageSize: OUTPUT_PAGE_SIZE,
        })
            .then((r) => { if (active) setResult(r); })
            .catch((ex) => { if (active) setError(ErrorUtils.getMessage(ex)); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [executed, outputKey, sheetId, sheet.sheetVersion, page]);

    const noun = segmentNoun(sheet.segmentType);
    const title = `Allocation by ${noun}`;
    if (!executed) {
        return (
            <div data-class="dasAllocationOutput">
                <Header variant="h3">{title}</Header>
                <Box color="text-body-secondary">{NOT_EXECUTED_MESSAGE}</Box>
            </div>
        );
    }

    const rows = result?.rows ?? [];
    const companies = result?.companies ?? [];
    const total = result?.totalSize ?? 0;
    const firstShown = ((result?.page ?? page) - 1) * (result?.pageSize ?? OUTPUT_PAGE_SIZE) + 1;
    const shown = rows.length === 0 ? `0 of ${total}` : `${firstShown}-${firstShown + rows.length - 1} of ${total}`;
    const description = `Each ${noun}'s allocation group from the mapping, and the share of each recipient company from the `
        + `last run, which the engine applies to the ${noun}'s balance. Showing ${shown}.`;
    const columns: TableProps.ColumnDefinition<DASOutputRow>[] = [
        { id: 'segment', header: noun.charAt(0).toUpperCase() + noun.slice(1), cell: (r) => r.segmentValue },
        { id: 'description', header: 'Description', cell: (r) => r.segmentDescription || '-' },
        { id: 'group', header: 'Allocation group', cell: (r) => r.groupName || '-' },
        ...companies.map((company, index) => ({
            id: `company-${index}`,
            header: company,
            // Matched by company code, so a row without some company's share still lines up.
            cell: (r: DASOutputRow) => services.formattingService.formatPercentage(
                r.shares?.find((s) => s.companyCode === company)?.share ?? undefined),
        })),
    ];

    return (
        <div data-class="dasAllocationOutput">
            {!!error && <Alert type="error" header="Unable to load the allocation">{error}</Alert>}
            <Table
                variant="embedded"
                wrapLines={false}
                loading={loading}
                loadingText="Loading the allocation"
                items={rows}
                header={<Header variant="h3" description={loading || error ? undefined : description}>{title}</Header>}
                empty={error ? null : <Box textAlign="center" color="inherit">The last run allocated no segments</Box>}
                pagination={(
                    <Pagination currentPageIndex={result?.page ?? page}
                                pagesCount={Math.max(1, Math.ceil(total / (result?.pageSize ?? OUTPUT_PAGE_SIZE)))}
                                disabled={loading}
                                onChange={({ detail }) => setPicked({ key: outputKey, page: detail.currentPageIndex })} />
                )}
                columnDefinitions={columns}
            />
        </div>
    );
}
